import React, { useCallback, useState } from 'react';
import { SparkIcon, Text } from '@spark';
import MappedValueHighlight from '@/shared/components/MappedValueHighlight';
import TemplateEditSurface from '@/shared/components/TemplateEditSurface';
import {
    EXAMPLE_TOKEN,
    resolvePromptPreviewSegments,
    tokenizePromptTemplate,
} from '../utils/promptTemplate';

/** Blue context-field / teal model-input pill (Figma .ModelSetup-PromptToken). */
export const PromptToken = ({ name, showBraces = false }) => {
    const isModelInput = name === EXAMPLE_TOKEN;
    return (
        <span
            className={`model-setup-prompt-token${isModelInput ? ' model-setup-prompt-token--input' : ''} fs-unmask`}
            data-token={name}
            // Inside the edit surface the pill is an atomic, non-editable
            // island; data-field is what serializes it back to {{name}}.
            data-field={name}
            contentEditable={false}
        >
            {isModelInput ? (
                <>
                    <SparkIcon slug="pin" size="small" />
                    example
                </>
            ) : (
                showBraces ? `{{${name}}}` : name
            )}
        </span>
    );
};

/**
 * Read-only inline render of a prompt template: `{{token}}` placeholders as
 * pills, plain text as-is. Shared by the SpecificAI tab's inherited-prompt
 * preview.
 */
export const PromptTemplateSegments = ({ value }) =>
    tokenizePromptTemplate(value || '').map((segment, index) =>
        segment.type === 'token' ? (
            <PromptToken
                key={`token-${index}`}
                name={segment.name}
                showBraces={segment.name !== EXAMPLE_TOKEN}
            />
        ) : (
            <span key={`text-${index}`}>{segment.value}</span>
        )
    );

/**
 * User Prompt template editor (Figma .ModelSetup-PromptEditor).
 *
 * Template mode is the shared TemplateEditSurface (same machinery as the Map
 * prompt template modal): `{{token}}` placeholders stay rendered as pills
 * while editing, and Cmd/Ctrl+Z undoes edits including deleted pills.
 * `onChange` receives the new template string. Preview mode is a read-only
 * render with tokens resolved to the current sample values.
 */
const PromptTemplateEditor = ({
    value,
    onChange,
    mode = 'template',
    disabled = false,
    placeholder = 'Write your model prompt.',
    exampleValue = '',
    paramValues = {},
    error = null,
    ariaLabel = 'User prompt',
    stretch = false,
    className = '',
}) => {
    // Both modes default to the rail-aligned height (CSS); a manual vertical resize sets an
    // inline height on the live element only, so it is recorded and re-applied
    // across surface remounts and the template/preview swap. Stretch mode
    // fills the parent instead, so a recorded height would pin the box short.
    const [boxHeight, setBoxHeight] = useState(null);

    const recordBoxHeight = (element) => {
        if (!element) return;
        const height = element.offsetHeight;
        if (height > 0) setBoxHeight(height);
    };

    // Flat children for the edit surface: text as-is, tokens as atomic pills.
    // Text segments must be plain fragments (no wrapper spans) so the surface
    // serialization/offset math sees flat text nodes.
    const renderEditChildren = useCallback(
        (template) =>
            tokenizePromptTemplate(template).map((segment, index) =>
                segment.type === 'token' ? (
                    <PromptToken
                        key={`token-${index}`}
                        name={segment.name}
                        showBraces={segment.name !== EXAMPLE_TOKEN}
                    />
                ) : (
                    // eslint-disable-next-line react/no-array-index-key
                    <React.Fragment key={`text-${index}`}>{segment.value}</React.Fragment>
                )
            ),
        []
    );

    const renderPreviewSegments = () => {
        const segments = resolvePromptPreviewSegments(value || '', {
            exampleValue,
            paramValues,
        });
        if (segments.length === 0) {
            return (
                <Text variant="base-regular" color="tertiary" as="span" className="fs-unmask">
                    {placeholder}
                </Text>
            );
        }
        return segments.map((segment, index) => {
            if (segment.type === 'text') {
                return <span key={`text-${index}`}>{segment.value}</span>;
            }
            if (segment.missing) {
                return <PromptToken key={`missing-${index}`} name={segment.name} showBraces={segment.name !== EXAMPLE_TOKEN} />;
            }
            // Same treatment as the Map prompt template "Show original text"
            // view: underlined value with a tooltip naming the source field —
            // green for the model input, brand blue for dataset columns.
            return (
                <MappedValueHighlight
                    key={`value-${index}`}
                    name={segment.name}
                    value={segment.value}
                    kind={segment.name === EXAMPLE_TOKEN ? 'custom' : 'column'}
                    data-token={segment.name}
                />
            );
        });
    };

    const boxClassName = [
        'model-setup-prompt-editor__box',
        mode === 'preview' ? 'model-setup-prompt-editor__box--preview' : '',
        disabled ? 'model-setup-prompt-editor__box--disabled' : '',
        error ? 'model-setup-prompt-editor__box--error' : '',
    ].filter(Boolean).join(' ');

    const boxStyle = !stretch && boxHeight ? { height: boxHeight } : undefined;
    const handleBoxMouseUp = stretch
        ? undefined
        : (e) => recordBoxHeight(e.currentTarget);

    return (
        <div className={['model-setup-prompt-editor', stretch ? 'model-setup-prompt-editor--stretch' : '', className].filter(Boolean).join(' ')}>
            {mode === 'template' ? (
                <TemplateEditSurface
                    template={value || ''}
                    editable={!disabled}
                    className={`${boxClassName} model-setup-prompt-editor__box--edit`}
                    style={boxStyle}
                    ariaLabel={ariaLabel}
                    ariaInvalid={Boolean(error)}
                    placeholder={placeholder}
                    renderChildren={renderEditChildren}
                    onTemplateChange={(next) => onChange?.(next)}
                    onMouseUp={handleBoxMouseUp}
                />
            ) : (
                <div
                    className={boxClassName}
                    style={boxStyle}
                    aria-label={ariaLabel}
                    aria-readonly
                    onMouseUp={handleBoxMouseUp}
                >
                    <div className="model-setup-prompt-editor__content">
                        {renderPreviewSegments()}
                    </div>
                </div>
            )}
            {error && (
                <div className="model-setup-prompt-editor__error fs-unmask" role="alert">
                    {error}
                </div>
            )}
        </div>
    );
};

export default PromptTemplateEditor;
